// Columna derecha del explorer (`.md-detail` del mock): la ficha del cliente con sus compras
// contigo, o la de quien solo abrió su cuenta. Se monta con `key` por cliente.
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, Mail, Phone, RefreshCw, Users } from 'lucide-react';
import type {
    PortalCustomerDetail,
    PortalCustomerRow,
} from '@/api/services/retail-wizard/contract';
import { Button, ErrorState, Skeleton } from '@/kit';
import {
    CustomerMessagesTab,
    MessageMomentCategoryEnum,
    useCanSeeCustomerMessages,
} from '@/shared/patterns/CustomerMessages';
import { cn } from '@/lib/cn';
import { formatDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import { formatPhone } from '../lib/customersFormat';
import {
    accountAgeLabel,
    daysLabel,
    distinctSkuCount,
    initialsOf,
    repurchaseReason,
} from '../lib/portalCustomerFormat';
import { CustomerContactCard } from './CustomerContactCard';
import { CustomerOtherStoresTab } from './CustomerOtherStoresTab';
import { CustomerPurchasesTab } from './CustomerPurchasesTab';
import { LevelPill, StatusChips } from './CustomersTable';
import { DetailCard } from './DetailCard';

type DetailTab = 'products' | 'otherStores' | 'messages';

export const ACCOUNT_ONLY_CARD_TITLE = 'Ya tiene cuenta, aún no compra';
export const START_SALE_LABEL = 'Iniciar venta';

export function CustomerDetailPanel({
    detail,
    accountOnlyRow = null,
    isLoading,
    notFound,
    failed,
    onRetry,
    hasSelection,
}: {
    detail: PortalCustomerDetail | null;
    /** La fila elegida cuando es de quien solo abrió su cuenta: no hay compras que leer. */
    accountOnlyRow?: PortalCustomerRow | null;
    isLoading: boolean;
    /** El silo dijo que el cliente no es de la cartera del vendedor. */
    notFound: boolean;
    /** La lectura falló por otra causa: se ofrece reintentar. */
    failed: boolean;
    onRetry: () => void;
    hasSelection: boolean;
}) {
    if (!hasSelection) {
        return (
            <EmptyDetail
                title="Selecciona un cliente"
                subtitle="Elige una fila de la izquierda para ver su historial contigo."
            />
        );
    }
    if (accountOnlyRow) return <AccountOnlyFile row={accountOnlyRow} />;
    if (notFound) {
        return (
            <EmptyDetail
                title="Este cliente no está en tu cartera"
                subtitle="Solo ves la ficha de quien te ha comprado a ti."
            />
        );
    }
    if (failed) {
        return (
            <ErrorState
                className="m-3"
                title="No se pudo leer la ficha del cliente."
                description="Vuelve a intentarlo; si sigue igual, avísale a tu gerente."
                onRetry={onRetry}
            />
        );
    }
    if (isLoading || detail === null) return <DetailSkeleton />;
    return <CustomerFile detail={detail} />;
}

function CustomerFile({ detail }: { detail: PortalCustomerDetail }) {
    const [tab, setTab] = useState<DetailTab>('products');
    const canSeeMessages = useCanSeeCustomerMessages(MessageMomentCategoryEnum.SALE);
    const { summary } = detail;

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border bg-card px-5 py-4">
                <div className="mb-3 flex min-w-0 items-center gap-3.5">
                    <div
                        aria-hidden
                        className="flex size-12 flex-none items-center justify-center rounded-full bg-primary text-[17px] font-bold text-primary-foreground"
                    >
                        {initialsOf(detail.customerName)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-[18px] font-bold tracking-[-0.01em]">
                                {detail.customerName ?? 'Cliente sin nombre'}
                            </h2>
                            <StatusChips
                                status={summary.status}
                                repurchase={summary.repurchaseCandidate}
                            />
                            <LevelPill
                                clientLevel={detail.clientLevel}
                                prefix="Nivel SK · "
                                unread={detail.creditUnavailable}
                            />
                        </div>
                        <div className="mt-[3px] text-[11px] text-muted-foreground">
                            cliente tuyo desde {formatDateMx(summary.firstPurchaseAt)}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-2">
                    <CustomerContactCard
                        icon={<Phone className="size-3.5" aria-hidden />}
                        label="Teléfono"
                        value={detail.customerPhone ? formatPhone(detail.customerPhone) : null}
                        copyValue={detail.customerPhone ?? undefined}
                    />
                    <CustomerContactCard
                        icon={<Mail className="size-3.5" aria-hidden />}
                        label="Correo"
                        value={detail.customerEmail}
                    />
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-muted/15 px-[18px] py-3.5">
                <div className="mb-3.5 grid grid-cols-2 gap-1.5 2xl:grid-cols-4">
                    <Kpi
                        label="Compras contigo"
                        value={summary.purchasesCount.toLocaleString('es-MX')}
                    />
                    <Kpi label="Total contigo" value={formatMXN(summary.totalSoldCents / 100)} />
                    <Kpi label="Ticket promedio" value={formatMXN(summary.avgTicketCents / 100)} />
                    <Kpi
                        label="Días s/comprarte"
                        value={daysLabel(summary.daysSinceLastPurchase)}
                    />
                </div>

                {summary.repurchaseCandidate ? (
                    <DetailCard
                        tone="warning"
                        title={
                            <span className="inline-flex items-center gap-1">
                                <RefreshCw className="size-3" aria-hidden />
                                Candidato a recompra
                            </span>
                        }
                    >
                        <p className="text-[11.5px] leading-normal">{repurchaseReason(summary)}</p>
                        <p className="mt-2 text-[10.5px] text-muted-foreground">
                            Contáctalo para ofrecerle renovar su equipo o una promoción vigente.
                        </p>
                    </DetailCard>
                ) : null}

                <div
                    role="tablist"
                    aria-label="Secciones de la ficha"
                    className="mb-3 flex border-b border-border bg-card"
                >
                    <TabButton
                        active={tab === 'products'}
                        count={distinctSkuCount(detail.purchases)}
                        onClick={() => {
                            setTab('products');
                        }}
                    >
                        Productos
                    </TabButton>
                    <TabButton
                        active={tab === 'otherStores'}
                        count={detail.otherStores.length}
                        onClick={() => {
                            setTab('otherStores');
                        }}
                    >
                        Otras tiendas
                    </TabButton>
                    {canSeeMessages ? (
                        <TabButton
                            active={tab === 'messages'}
                            onClick={() => {
                                setTab('messages');
                            }}
                        >
                            Mensajes
                        </TabButton>
                    ) : null}
                </div>

                <div role="tabpanel">
                    {tab === 'products' ? <CustomerPurchasesTab detail={detail} /> : null}
                    {tab === 'otherStores' ? (
                        <CustomerOtherStoresTab stores={detail.otherStores} />
                    ) : null}
                    {tab === 'messages' && canSeeMessages ? (
                        <CustomerMessagesTab
                            retailCustomerId={detail.retailCustomerId}
                            category={MessageMomentCategoryEnum.SALE}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
}

/** Ficha de quien abrió su cuenta PCF en la tienda y aún no compra; «Iniciar venta» abre el wizard. */
function AccountOnlyFile({ row }: { row: PortalCustomerRow }) {
    const navigate = useNavigate();
    const phone = row.customerPhone;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-none border-b border-border bg-card px-5 py-4">
                <div className="mb-3 flex min-w-0 items-center gap-3.5">
                    <div
                        aria-hidden
                        className="flex size-12 flex-none items-center justify-center rounded-full bg-primary text-[17px] font-bold text-primary-foreground"
                    >
                        {initialsOf(row.customerName)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-[18px] font-bold tracking-[-0.01em]">
                                {row.customerName ?? 'Cliente sin nombre'}
                            </h2>
                            <StatusChips status="ACCOUNT_ONLY" />
                        </div>
                        {row.createdAt ? (
                            <div className="mt-[3px] text-[11px] text-muted-foreground">
                                abrió su cuenta el {formatDateMx(row.createdAt)} ·{' '}
                                {accountAgeLabel(row.createdAt)}
                            </div>
                        ) : null}
                    </div>
                </div>
                <CustomerContactCard
                    icon={<Phone className="size-3.5" aria-hidden />}
                    label="Teléfono"
                    value={phone ? formatPhone(phone) : null}
                    copyValue={phone ?? undefined}
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-muted/15 px-[18px] py-3.5">
                <DetailCard tone="info" title={ACCOUNT_ONLY_CARD_TITLE}>
                    <p className="text-[11.5px] leading-normal">
                        Abrió su cuenta PCF en tu tienda y todavía no ha comprado. Ofrécele su
                        primera compra.
                    </p>
                    {phone ? (
                        <Button
                            size="sm"
                            className="mt-2 self-start"
                            rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                            onClick={() => void navigate({ to: '/wizard', search: { phone } })}
                        >
                            {START_SALE_LABEL}
                        </Button>
                    ) : null}
                </DetailCard>
            </div>
        </div>
    );
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-col gap-0.5 rounded-md border border-border bg-card px-2.5 py-2">
            <div className="text-[9px] font-bold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </div>
            <div className="text-[13px] font-bold leading-[1.15] tabular-nums">{value}</div>
        </div>
    );
}

function TabButton({
    active,
    count,
    onClick,
    children,
}: {
    active: boolean;
    count?: number;
    onClick: () => void;
    children: string;
}) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3.5 py-2 text-[12px] transition-colors',
                active
                    ? 'border-primary font-semibold text-foreground'
                    : 'border-transparent font-medium text-muted-foreground hover:text-foreground',
            )}
        >
            {children}
            {count === undefined ? null : (
                <span
                    className={cn(
                        'inline-flex items-center rounded-[3px] px-[5px] text-[10px] font-bold tabular-nums',
                        active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

function EmptyDetail({ title, subtitle }: { title: string; subtitle: string }) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center text-muted-foreground">
            <Users className="mb-3 size-[42px] text-muted-foreground/40" aria-hidden />
            <div className="mb-1 text-[13px] font-semibold text-foreground">{title}</div>
            <div className="text-[11.5px]">{subtitle}</div>
        </div>
    );
}

function DetailSkeleton() {
    return (
        <div className="flex flex-col gap-3 p-5" aria-busy>
            <div className="flex items-center gap-3.5">
                <Skeleton className="size-12 rounded-full" />
                <Skeleton className="h-5 w-48" />
            </div>
            <Skeleton className="h-12" />
            <Skeleton className="h-24" />
            <Skeleton className="h-40" />
        </div>
    );
}
